/**
 * Schemas declarativos del Toolbar y Subheader del LayoutBuilder.
 *
 * Cada función devuelve datos puros (sin acceso a Vue/DOM/refs). El componente
 * los consume desde computeds en una sola línea; cualquier otro consumidor
 * (vista de edición, preview, etc.) puede reusarlos importándolos.
 *
 * Las funciones que aparecen dentro de los items (active, disabled, visible, etc.)
 * reciben el `context` que se pasa al Toolbar/Subheader y se evalúan en runtime.
 */

import { PICTOGRAM_TYPES } from "../../constants/pictogram-types"

/**
 * Schema de los botones del Toolbar.
 * @returns {Array} buttons en el formato esperado por el componente Toolbar
 */
export function buildToolbarButtons() {
  return [
    {
      id:         'panel-layers',
      type:       'toggle',
      group:      'layers',
      icon:       'layer-group',
      tooltipKey: 'layers',
      active:     ctx => ctx.showLayerPanel,
      handler:    'toggle-layers-panel',
    },
    { id: 'pan',        type: 'toggle', group: 'nav', icon: 'hand-paper',
      active: ctx => ctx.isDraggable, handler: 'pan' },
    { id: 'fit',        type: 'button', group: 'nav', icon: 'arrows-to-circle', handler: 'fit' },
    { id: 'zoom-in',    type: 'button', group: 'nav', icon: 'search-plus',      handler: 'zoom-in' },
    { id: 'zoom-out',   type: 'button', group: 'nav', icon: 'search-minus',     handler: 'zoom-out' },
    { id: 'fullscreen', type: 'button', group: 'nav', icon: 'expand',           handler: 'fullscreen' },
    { id: 'configure',  type: 'button', group: 'config', icon: 'gear',
      tooltipKey: 'configure_subdivisions',
      // El proceso de subdivisiones (pilings, modules, panels) es exclusivo de
      // mechanical_installation. Para electrical/trenches/evacuation no aplica
      // — se oculta el botón.
      visible: ctx => ctx.typePictogram === PICTOGRAM_TYPES.MECHANICAL,
      handler: 'configure' },
    // "Limpiar diseño" — solo en design (admin). El cliente en progress
    // mode no puede descartar el .epcb que se descargó del backend.
    { id: 'reset',      type: 'button', group: 'reset',  icon: 'eraser',
      tooltipKey: 'clear_viewer',
      visible: ctx => ctx.viewerMode !== 'progress',
      handler: 'reset' },
    // En electrical/trenches el flujo es "Procesar → Modal de capas → Guardar",
    // análogo al "Configurar → Modal subdivisiones → Guardar" del mecánico.
    // El botón "Procesar" abre SelectLayersModal donde se eligen las capas
    // a registrar; al confirmar se encadena con SavePictogramModal.
    // En viewerMode='progress' (cliente registrando avance) ambos botones
    // se ocultan — el cliente no edita el diseño, solo registra avance.
    { id: 'process',    type: 'button', group: 'save',   icon: 'list-check',
      tooltipKey: 'process',
      visible: ctx => ctx.permissions.save
        && ctx.typePictogram !== PICTOGRAM_TYPES.MECHANICAL
        && ctx.viewerMode !== 'progress',
      handler: 'process' },
    { id: 'save',       type: 'button', group: 'save',   icon: 'save',
      visible: ctx => ctx.permissions.save
        && ctx.typePictogram === PICTOGRAM_TYPES.MECHANICAL
        && ctx.viewerMode !== 'progress',
      handler: 'save' },
    // Dropdown "Resaltar" — SOLO en progress mode (cliente registrando avance)
    // y en no-mechanical. Ofrece dos filtros visuales de un vistazo:
    //   - sin avance: pinta con un color uniforme los elementos trackeables
    //     que todavía NO tienen avance registrado (pendientes).
    //   - con avance: pinta con un color uniforme los que SÍ tienen avance.
    // Es una feature configurable por el admin (features.showHighlightMenu),
    // activa por defecto. `active` marca el botón mientras haya un modo puesto.
    { id: 'highlight',  type: 'dropdown', group: 'highlight', mainIcon: 'fill-drip',
      handler: 'highlight',
      // Visible por defecto: solo se oculta si el admin pone explícitamente
      // show_highlight_menu === false. Si features no trae la flag, se muestra.
      visible: ctx => ctx.viewerMode === 'progress'
        && ctx.typePictogram !== PICTOGRAM_TYPES.MECHANICAL
        && (ctx.features || {}).showHighlightMenu !== false,
      active: ctx => !!ctx.highlightMode,
      defaultOptionValue: 'without',
      options: [
        { value: 'without', labelKey: 'highlight_without_progress', icon: 'circle-notch' },
        { value: 'with',    labelKey: 'highlight_with_progress',    icon: 'circle-check' },
      ],
    },
    // Dropdown "Ver avance" — controla QUÉ estados se pintan sobre cada
    // elemento. Por defecto solo el último estado registrado; el usuario puede
    // pasar a "todos" o a un estado puntual de la capa activa. Configurable por
    // el admin (features.showStatusFilter), visible por defecto.
    { id: 'status-view', type: 'dropdown', group: 'status-view', mainIcon: 'eye',
      handler: 'status-view',
      // Mientras el resaltado (sin/con avance) está activo, este control se
      // oculta: su efecto queda anulado porque toda la escena está en gris y
      // manda el overlay de resaltado.
      visible: ctx => ctx.viewerMode === 'progress'
        && ctx.typePictogram !== PICTOGRAM_TYPES.MECHANICAL
        && !ctx.highlightMode
        && (ctx.features || {}).showStatusFilter !== false,
      active: ctx => ctx.statusViewMode && ctx.statusViewMode !== 'last',
      defaultOptionValue: 'last',
      // Opciones dinámicas: "último" + "todos" + un item por cada estado de la
      // capa activa (si hay una activa con estados configurados).
      options: ctx => [
        { value: 'last', labelKey: 'status_view_last', icon: 'clock-rotate-left' },
        { value: 'all',  labelKey: 'status_view_all',  icon: 'layer-group' },
        ...((ctx.activeLayerStatuses || []).map(s => ({
          value: s.value,
          label: s.label,
          icon:  'circle',
        }))),
      ],
    },
  ]
}

/**
 * Estado expuesto al Toolbar para resolver `active`, `visible`, `disabled` de cada item.
 */
export function buildToolbarContext({ pictogram, showLayerPanel, isDraggable, allowSave, typePictogram, viewerMode, features, highlightMode, statusViewMode, activeLayerStatuses }) {
  return {
    pictogram,
    typePictogram,
    viewerMode,
    showLayerPanel,
    isDraggable,
    permissions: { save: !!allowSave },
    // Flags de features configurables por el admin (default true en el
    // componente). Se leen en `visible` de los items gateados.
    features: features || {},
    // Modo de resaltado activo ('without' | 'with' | null) — marca el
    // dropdown "Resaltar" como activo mientras haya uno puesto.
    highlightMode: highlightMode || null,
    // Modo de visualización de estados ('last' | 'all' | <statusValue>) y los
    // estados de la capa activa (para las opciones dinámicas del dropdown).
    statusViewMode: statusViewMode || 'last',
    activeLayerStatuses: activeLayerStatuses || [],
  }
}

/**
 * Schema de los items del Subheader.
 * Estado inicial (sin archivo): único botón "Subir archivo" a la derecha + volver.
 * Estado normal (con archivo): botones de Vistas y Capas a la izquierda + volver.
 *
 * @param {Object}  opts
 * @param {boolean} opts.hasFile - si el usuario ya cargó un archivo CAD
 */
export function buildSubheaderItems({ hasFile }) {
  const back = {
    id: 'back', type: 'button', side: 'right',
    icon: 'rotate-left', labelKey: 'back', handler: 'back',
  }

  if (!hasFile) {
    return [
      // El botón de subir archivo solo aparece en 'design' (admin). En
      // 'progress' (cliente) la escena viene del .epcb del backend — el
      // cliente no carga archivos.
      { id: 'upload', type: 'button', side: 'right',
        icon: 'upload', labelKey: 'upload_file', handler: 'upload-file',
        visible: ctx => ctx.viewerMode !== 'progress' },
      back,
    ]
  }

  return [
    {
      id: 'views', type: 'button', side: 'left',
      icon: 'window-restore', labelKey: 'views',
      handler:  'open-panel-views',
      active:   ctx => ctx.showPanelViews,
      disabled: ctx => !ctx.hasProcessed,
    },
    {
      id: 'layers', type: 'button', side: 'left',
      icon: 'layer-group', labelKey: 'layers',
      handler: 'open-panel-layers',
      active:  ctx => ctx.showLayerPanel,
    },
    back,
  ]
}

/**
 * Estado expuesto al Subheader para resolver `active`, `disabled` de cada item.
 */
export function buildSubheaderContext({ hasProcessed, showPanelViews, showLayerPanel, viewerMode }) {
  return { hasProcessed, showPanelViews, showLayerPanel, viewerMode }
}
